import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSection8, S8_STATUSES } from "@/hooks/use-section8";
import { usePipeline } from "@/hooks/use-pipeline";
import { MockProperty } from "@/data/mockProperties";
import { Users, Home, DollarSign, Calendar } from "lucide-react";

export function VoucherMatchingTab() {
  const { cases } = useSection8();
  const { savedProperties } = usePipeline();

  // Cases with tenant/voucher info
  const matchedCases = cases.filter((c) => c.tenant_name || c.tenant_voucher_amount);
  const unmatchedCases = cases.filter((c) => !c.tenant_name && !c.tenant_voucher_amount);

  const getProperty = (spId: string) => {
    const sp = savedProperties.find((s) => s.id === spId);
    return sp?.property_data as unknown as MockProperty | undefined;
  };

  const getStatusInfo = (status: string) =>
    S8_STATUSES.find((s) => s.value === status) || S8_STATUSES[0];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Voucher Matching</h3>
        <p className="text-sm text-muted-foreground">Track voucher holders and match them to your available units</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary"><Users className="h-5 w-5" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Matched Tenants</p>
              <p className="text-xl font-bold text-foreground">{matchedCases.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/10 text-amber-500"><Home className="h-5 w-5" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Awaiting Match</p>
              <p className="text-xl font-bold text-foreground">{unmatchedCases.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-500"><DollarSign className="h-5 w-5" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total Voucher Value</p>
              <p className="text-xl font-bold text-foreground">
                ${matchedCases.reduce((s, c) => s + Number(c.tenant_voucher_amount || 0), 0).toLocaleString()}/mo
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Matched tenants */}
      {matchedCases.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Matched Voucher Holders</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matchedCases.map((c) => {
              const prop = getProperty(c.pipeline_property_id);
              const status = getStatusInfo(c.status);
              return (
                <Card key={c.id} className="shadow-soft">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-foreground">{c.tenant_name}</p>
                        <p className="text-xs text-muted-foreground">{c.tenant_phone || c.tenant_email || "No contact"}</p>
                      </div>
                      <Badge variant="outline" className={status.color}>{status.label}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Property</p>
                        <p className="text-foreground truncate">{prop?.address || "Unknown"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Voucher Amount</p>
                        <p className="text-foreground font-medium">${Number(c.tenant_voucher_amount || 0).toLocaleString()}/mo</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Move-in Date</p>
                        <p className="text-foreground">{c.tenant_move_in_date || "TBD"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">HAP Contract</p>
                        <p className="text-foreground">{c.hap_contract_start ? `${c.hap_contract_start} → ${c.hap_contract_end || "Ongoing"}` : "Pending"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Unmatched properties */}
      {unmatchedCases.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Properties Awaiting Voucher Tenant</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unmatchedCases.map((c) => {
              const prop = getProperty(c.pipeline_property_id);
              return (
                <Card key={c.id} className="shadow-soft">
                  <CardContent className="p-4">
                    <p className="font-medium text-foreground text-sm truncate">{prop?.address || "Unknown"}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {prop?.bedrooms || "?"} bed / {prop?.bathrooms || "?"} bath
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-muted-foreground">
                        Asking: {c.asking_rent ? `$${Number(c.asking_rent).toLocaleString()}` : "Not set"}
                      </span>
                      <Badge variant="outline" className="text-[10px]">Needs tenant</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {cases.length === 0 && (
        <Card className="shadow-soft">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No Section 8 cases yet. Create cases from the Cases tab first.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
